import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { AccountInput, ExtensionTypes } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { createAccountInput } from 'src/app/common-components/helpers/account-helper';
import { coerceToNullableNumber } from 'src/app/common-components/helpers/type-helper';
import { AccountDetailSaveModel } from '../../account-details/models/account-detail-save.model';
import { SaveAccount, SaveAccountVariables } from '../../account-details/queries/generated/SaveAccount';
import { SaveAccountMutation } from '../../account-details/queries/save-account.graphql';

@Injectable({ providedIn: 'root' })
export class AddAccountApiService {
   constructor(private httpClient: HttpClient, private apiService: LcsApiService) {}

   create(accountDetail: AccountDetailSaveModel): Observable<ExecutionResult<SaveAccount>> {
      const fields: (keyof AccountInput)[] = [
         'name',
         'isRecordInternalCalls',
         'isRecordOutboundCalls',
         'isRecordInboundCalls',
         'isOtrEnabled',
         'pstnLineLimit',
         'shortName',
         'timeZoneId',
         'activeStartDate',
         'lcsAccountNumber',
         'isResidential',
         'versionId',
      ];
      const account = createAccountInput(accountDetail, fields);

      const variables: SaveAccountVariables = {
         deletedNoteIds: [],
         account: account,
      };

      return this.apiService.query<SaveAccount, SaveAccountVariables>({
         query: SaveAccountMutation,
         variables: variables,
      });
   }

   createNewAccount(accountDetail: AccountDetailSaveModel) {
      const accountPostModel = this.createAccountPostModel(accountDetail);
      return this.httpClient.post<number>(this.apiService.apiUrl + '/Accounts/SaveNewAccount', accountPostModel, {
         observe: 'response',
      });
   }

   private createAccountPostModel(accountDetail: AccountDetailSaveModel) {
      return {
         Name: accountDetail.name,
         ShortName: accountDetail.shortName,
         ActiveStartDate: accountDetail.activeStartDate,
         LcsAccountNumber: coerceToNullableNumber(accountDetail.lcsAccountNumber),
         TimeZoneId: accountDetail.timeZoneId,
         IsResidential: accountDetail.isResidential ?? false,
         PstnLineLimit: accountDetail.pstnLineLimit,
         IsRecordInboundCalls: accountDetail.isRecordInboundCalls ?? false,
         IsRecordOutboundCalls: accountDetail.isRecordOutboundCalls ?? false,
         IsRecordInternalCalls: accountDetail.isRecordInternalCalls ?? false,
         IsOtrEnabled: accountDetail.isOtrEnabled ?? false,
         VersionId: accountDetail.versionId,
         Locations: accountDetail.newAccountLocations
            ? accountDetail.newAccountLocations.map((location) => {
                 return {
                    Name: location?.name,
                    HouseNumber: location?.houseNumber,
                    DirectionPrefix: location?.directionalPrefix,
                    StreetName: location?.streetName,
                    StreetNameSuffix: location?.streetNameSuffix,
                    DirectionPostFix: location?.directionalPostfix,
                    City: location?.city,
                    State: location?.state,
                    PostalCode: location?.postalCode,
                    Country: location?.country,
                    Comment: location?.comment,
                    TimeZoneId: location?.timeZoneId,
                 };
              })
            : [],
         Extensions: accountDetail.newExtensions?.map((extension) => {
            switch (extension.extension.type) {
               case ExtensionTypes.AutoAttendant:
                  return {
                     Name: extension.extension.name,
                     Number: extension.extension.number,
                     Type: extension.extension.type,
                     ExtensionAutoAttendant: {
                        TimeoutSeconds: extension.extension.extensionAutoAttendant?.timeoutSeconds,
                        AutoAttendantKeys: extension.extension.extensionAutoAttendant?.autoAttendantKeys?.map(
                           (key: any) => {
                              return {
                                 KeyNumber: key.keyNumber,
                                 RouteToExtension:
                                    key.routeToExtension !== 'Name Directory' &&
                                    key.RouteToExtension !== 'Hangup' &&
                                    key.routeToExtension != null
                                       ? {
                                            Number: key.routeToExtension.number,
                                         }
                                       : null,
                                 VoicemailOnly: false,
                                 routeToNullAction: key.routeToNullAction,
                              };
                           }
                        ),
                     },
                  };
               case ExtensionTypes.HuntGroup:
                  return {
                     Name: extension.extension.name,
                     Number: extension.extension.number,
                     Type: extension.extension.type,
                     ExtensionHuntGroup: {
                        RingTimeout: extension.extension.extensionHuntGroup?.ringTimeOut,
                     },
                  };
               case ExtensionTypes.ExternalForward:
                  return {
                     Name: extension.extension.name,
                     Number: extension.extension.number,
                     Type: extension.extension.type,
                     ExtensionExternalForward: {
                        ForwardToNumber: extension.extension.extensionExternalForward?.forwardToNumber,
                     },
                  };
               case ExtensionTypes.Fax:
                  return {
                     Name: extension.extension.name,
                     Number: extension.extension.number,
                     Type: extension.extension.type,
                     ExtensionFax: {
                        EmailAddress: extension.extension.extensionFax?.emailAddress,
                     },
                  };
               case ExtensionTypes.VoiceMailbox:
                  return {
                     Name: extension.extension.name,
                     Number: extension.extension.number,
                     Type: extension.extension.type,
                     Mailbox: {
                        EmailAddress: extension.extension.mailbox?.emailAddress,
                     },
                  };
            }
         }),
         PhoneNumbers: accountDetail.newPhoneNumbers?.map((phoneNumber) => {
            return {
               PhoneNumberId: phoneNumber.phoneNumberId,
               DisplayName: phoneNumber.displayName,
               RouteTo: phoneNumber.routeTo,
               ProviderId: phoneNumber.providerId,
               DatePorted: phoneNumber.datePorted,
               IsActive: phoneNumber.isActive,
               IsPorted: phoneNumber.isPorted,
               PhoneNumber: phoneNumber.phoneNumber,
            };
         }),
      };
   }
}
